import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, CalendarDays, Users } from 'lucide-react';

// Planning API
const API = process.env.REACT_APP_BACKEND_URL;

const planningAPI = {
  createSlot: async (data) => {
    const res = await fetch(`${API}/api/planning-slots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },
  updateSlot: async (id, data) => {
    const res = await fetch(`${API}/api/planning-slots/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },
  deleteSlot: async (id) => {
    const res = await fetch(`${API}/api/planning-slots/${id}`, {
      method: 'DELETE'
    });
    return res.json();
  }
};

// Constantes
const VT_TO_MIN = 5;
const HOURS = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
const JOURS_SEMAINE = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];

// Helper functions
const getWeekDates = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.getDay();
  const monday = new Date(date);
  monday.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  const dates = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};

const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

const fmtDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

// Technician colors palette
const TECH_COLORS = [
  { bg: 'rgba(59, 130, 246, 0.25)', border: '#3b82f6', text: '#bfdbfe' },
  { bg: 'rgba(139, 92, 246, 0.25)', border: '#8b5cf6', text: '#ddd6fe' },
  { bg: 'rgba(236, 72, 153, 0.25)', border: '#ec4899', text: '#fbcfe8' },
  { bg: 'rgba(245, 158, 11, 0.25)', border: '#f59e0b', text: '#fde68a' },
  { bg: 'rgba(16, 185, 129, 0.25)', border: '#10b981', text: '#a7f3d0' },
  { bg: 'rgba(239, 68, 68, 0.25)', border: '#ef4444', text: '#fecaca' },
];

const Planning = ({ slots, techniciens, ots, clients, motos, absences = [], onUpdate }) => {
  const [planningView, setPlanningView] = useState('jour');
  const [currentDate, setCurrentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showNewSlotModal, setShowNewSlotModal] = useState(false);
  const [draggedSlot, setDraggedSlot] = useState(null);
  const [newSlotForm, setNewSlotForm] = useState({
    technicien_id: '',
    date: currentDate,
    heure_debut: '09:00',
    duree_minutes: 60,
    ordre_travail_id: ''
  });

  // Filtered technicians (only workshop technicians)
  const techsForPlanning = useMemo(() => 
    techniciens.filter(t => !t.role || t.role === 'technicien' || t.role === 'Atelier'),
    [techniciens]
  );

  const weekDates = getWeekDates(currentDate);
  const today = new Date().toISOString().split('T')[0];

  // Helper functions
  const getClient = (id) => clients.find(c => c.id === id);
  const getMoto = (id) => motos.find(m => m.id === id);
  const getTech = (id) => techniciens.find(t => t.id === id);
  const getOT = (id) => ots.find(o => o.id === id);
  const getTechColor = (techId) => {
    const idx = techsForPlanning.findIndex(t => t.id === techId);
    return TECH_COLORS[idx % TECH_COLORS.length];
  };

  const slotsForDate = (date) => slots.filter(s => s.date === date && s.statut !== 'annule');

  // Check if technician is absent
  const isAbsent = (techId, date) => {
    return absences.find(a => 
      a.technicien_id === techId && 
      a.debut <= date && 
      a.fin >= date
    );
  };

  // Absence colors
  const TYPE_ABSENCE_COLORS = {
    conge: { label: 'Congé', cssClass: 'vacation' },
    maladie: { label: 'Maladie', cssClass: 'sick' },
    formation: { label: 'Formation', cssClass: 'vacation' },
    autre: { label: 'Absent', cssClass: 'sick' }
  };

  // Navigation
  const navWeek = (delta) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + delta * 7);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const navDay = (delta) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + delta);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setCurrentDate(today);
  };

  // Drag & Drop handlers
  const handleDragStart = (e, slot) => {
    setDraggedSlot(slot);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slot.id);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedSlot(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnCell = async (e, technicienId, date, heure) => {
    e.preventDefault();
    if (!draggedSlot) return;
    
    try {
      await planningAPI.updateSlot(draggedSlot.id, {
        technicien_id: technicienId,
        date: date,
        heure_debut: heure
      });
      onUpdate();
    } catch (err) {
      console.error('Error moving slot:', err);
    }
    setDraggedSlot(null);
  };

  const handleCreateSlot = async () => {
    try {
      await planningAPI.createSlot(newSlotForm);
      onUpdate();
      setShowNewSlotModal(false);
      setNewSlotForm({ technicien_id: '', date: currentDate, heure_debut: '09:00', duree_minutes: 60, ordre_travail_id: '' });
    } catch (err) {
      console.error('Error creating slot:', err);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await planningAPI.deleteSlot(slotId);
      onUpdate();
      setSelectedSlot(null);
    } catch (err) {
      console.error('Error deleting slot:', err);
    }
  };

  // Calculate current time position
  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const startHour = 7; // 07:00
    const totalMinutes = (hours - startHour) * 60 + minutes;
    return Math.max(0, totalMinutes);
  };

  // Calculate event position and height based on time
  const getEventStyle = (slot) => {
    const [hours, minutes] = slot.heure_debut.split(':').map(Number);
    const startHour = 7;
    const topPosition = (hours - startHour) * 60 + minutes;
    const height = slot.duree_minutes;
    return {
      top: `${topPosition}px`,
      height: `${Math.max(height, 30)}px`
    };
  };

  // Calculate KPI for technician
  const getTechKpi = (techId, forWeek = false) => {
    const tech = getTech(techId);
    if (!tech) return { vtTotal: 0, max: 0, pct: 0 };
    
    const dates = forWeek ? weekDates : [currentDate];
    const techSlots = dates.flatMap(d => slotsForDate(d).filter(s => s.technicien_id === techId));
    const vtTotal = techSlots.reduce((s, sl) => s + sl.duree_minutes, 0) / VT_TO_MIN;
    const max = forWeek ? tech.vt_jour_max * 5 : tech.vt_jour_max;
    const pct = max > 0 ? Math.round((vtTotal / max) * 100) : 0;
    
    return { vtTotal, max, pct };
  };

  // Format date for header
  const formatHeaderDate = () => {
    if (planningView === 'semaine') {
      return `Semaine ${getWeekNumber(weekDates[0])} — ${new Date(weekDates[0]).toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}`;
    } else {
      return new Date(currentDate).toLocaleDateString('fr-CH', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    }
  };

  return (
    <div className="gcal-container" data-testid="planning-view">
      {/* Header */}
      <div className="gcal-header">
        <div className="gcal-nav">
          <button 
            className="gcal-nav-btn" 
            onClick={() => planningView === 'semaine' ? navWeek(-1) : navDay(-1)}
            data-testid="planning-nav-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button 
            className="gcal-nav-btn"
            onClick={() => planningView === 'semaine' ? navWeek(1) : navDay(1)}
            data-testid="planning-nav-next"
          >
            <ChevronRight size={18} />
          </button>
          <button 
            className="gcal-today-btn"
            onClick={goToToday}
            data-testid="planning-today-btn"
          >
            Aujourd'hui
          </button>
          <span className="gcal-date-title">{formatHeaderDate()}</span>
        </div>

        <div className="flex items-center gap-3">
          <div className="gcal-view-toggle">
            <button 
              className={`gcal-view-btn ${planningView === 'jour' ? 'active' : ''}`}
              onClick={() => setPlanningView('jour')}
              data-testid="planning-view-day"
            >
              <Users size={14} style={{ marginRight: 6, display: 'inline' }} />
              Jour
            </button>
            <button 
              className={`gcal-view-btn ${planningView === 'semaine' ? 'active' : ''}`}
              onClick={() => setPlanningView('semaine')}
              data-testid="planning-view-week"
            >
              <CalendarDays size={14} style={{ marginRight: 6, display: 'inline' }} />
              Semaine
            </button>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => { setNewSlotForm({ ...newSlotForm, date: currentDate }); setShowNewSlotModal(true); }}
            data-testid="planning-new-rdv-btn"
          >
            <Plus size={14} /> Nouveau RDV
          </button>
        </div>
      </div>

      {/* KPI Bar */}
      <div className="gcal-kpi-bar">
        {techsForPlanning.map((tech, idx) => {
          const { vtTotal, max, pct } = getTechKpi(tech.id, planningView === 'semaine');
          const color = pct > 85 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#22c55e';
          const absence = planningView === 'jour' ? isAbsent(tech.id, currentDate) : null;
          const absenceInfo = absence ? TYPE_ABSENCE_COLORS[absence.type] || TYPE_ABSENCE_COLORS.autre : null;

          return (
            <div 
              key={tech.id} 
              className="gcal-kpi-card"
              data-testid={`tech-kpi-${tech.id}`}
              style={absence ? { 
                background: absence.type === 'maladie' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                border: `1px solid ${absence.type === 'maladie' ? '#ef4444' : '#22c55e'}`
              } : {}}
            >
              <div 
                className="gcal-kpi-progress"
                style={{ 
                  background: `conic-gradient(${color} ${pct * 3.6}deg, #27272a ${pct * 3.6}deg)`
                }}
              >
                <span style={{ color }}>{absence ? '—' : `${pct}%`}</span>
              </div>
              <div className="gcal-kpi-info">
                <div className="gcal-kpi-name">{tech.prenom} {tech.nom?.charAt(0)}.</div>
                <div className="gcal-kpi-detail">
                  {absence ? absenceInfo.label : `${vtTotal}/${max} VT`}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Calendar Body */}
      <div className="gcal-body">
        {/* Time Column */}
        <div className="gcal-time-column">
          <div style={{ height: '60px' }} /> {/* Spacer for header */}
          {HOURS.map(hour => (
            <div key={hour} className="gcal-time-cell">{hour}</div>
          ))}
        </div>

        {/* Grid */}
        <div 
          className={`gcal-grid ${planningView === 'jour' ? 'gcal-grid-day' : 'gcal-grid-week'}`}
          style={planningView === 'jour' ? { '--tech-count': techsForPlanning.length } : {}}
        >
          {planningView === 'jour' ? (
            /* ===== DAY VIEW ===== */
            techsForPlanning.map((tech, idx) => {
              const absence = isAbsent(tech.id, currentDate);
              const absenceInfo = absence ? TYPE_ABSENCE_COLORS[absence.type] || TYPE_ABSENCE_COLORS.autre : null;
              const daySlots = slotsForDate(currentDate).filter(s => s.technicien_id === tech.id);
              const techColor = getTechColor(tech.id);

              return (
                <div key={tech.id} className="gcal-column" data-testid={`day-column-${tech.id}`}>
                  {/* Column Header - Technician */}
                  <div className="gcal-column-header">
                    <div className="gcal-column-header-tech">
                      <div 
                        className="gcal-tech-avatar" 
                        style={{ background: tech.couleur_hex || techColor.border }}
                      >
                        {tech.initiales || (tech.prenom?.charAt(0) + tech.nom?.charAt(0))}
                      </div>
                      <div className="gcal-tech-name">{tech.prenom}</div>
                      {absence && (
                        <span 
                          className="gcal-absence-label" 
                          style={{ 
                            background: absence.type === 'maladie' ? '#ef4444' : '#22c55e',
                            fontSize: '10px',
                            padding: '2px 8px'
                          }}
                        >
                          {absenceInfo.label}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Column Body - Time slots */}
                  <div className="gcal-column-body" style={{ height: `${HOURS.length * 60}px`, position: 'relative' }}>
                    {/* Absence overlay */}
                    {absence && (
                      <div className={`gcal-absence ${absenceInfo.cssClass}`}>
                        <span className="gcal-absence-label">{absenceInfo.label}</span>
                      </div>
                    )}

                    {/* Hour rows */}
                    {HOURS.map((hour, hourIdx) => (
                      <div 
                        key={hour}
                        className={`gcal-hour-row ${draggedSlot ? 'cursor-copy' : ''}`}
                        style={{ background: draggedSlot ? 'rgba(232, 98, 26, 0.05)' : undefined }}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDropOnCell(e, tech.id, currentDate, hour)}
                        data-testid={`slot-${tech.id}-${hour}`}
                      />
                    ))}

                    {/* Current time indicator */}
                    {currentDate === today && (
                      <div 
                        className="gcal-current-time"
                        style={{ top: `${getCurrentTimePosition()}px` }}
                      />
                    )}

                    {/* Events */}
                    {!absence && daySlots.map(slot => {
                      const ot = getOT(slot.ordre_travail_id);
                      const client = ot ? getClient(ot.client_id) : null;
                      const moto = ot ? getMoto(ot.moto_id) : null;
                      const eventStyle = getEventStyle(slot);

                      return (
                        <div
                          key={slot.id}
                          className="gcal-event"
                          draggable
                          onDragStart={(e) => handleDragStart(e, slot)}
                          onDragEnd={handleDragEnd}
                          onClick={() => setSelectedSlot(slot)}
                          style={{
                            ...eventStyle,
                            background: techColor.bg,
                            borderLeftColor: techColor.border,
                            opacity: slot.statut === 'provisoire' ? 0.7 : 1
                          }}
                          data-testid={`event-${slot.id}`}
                        >
                          <div className="gcal-event-title">
                            {client?.nom || 'Sans client'}
                          </div>
                          <div className="gcal-event-subtitle">
                            {moto ? `${moto.marque} ${moto.modele}` : ot?.reference || 'RDV'}
                          </div>
                          <div className="gcal-event-time">
                            {slot.heure_debut} · {slot.duree_minutes}min
                          </div>
                          {slot.statut === 'provisoire' && (
                            <span className="badge badge-yellow" style={{ fontSize: '9px', marginTop: '4px' }}>
                              Provisoire
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            /* ===== WEEK VIEW ===== */
            weekDates.map((date, dateIdx) => {
              const daySlots = slotsForDate(date);
              const isToday = date === today;
              const dayDate = new Date(date);
              
              // Count absences for this day
              const absencesCount = techsForPlanning.filter(t => isAbsent(t.id, date)).length;

              return (
                <div 
                  key={date} 
                  className="gcal-column"
                  data-testid={`week-column-${date}`}
                >
                  {/* Column Header - Day */}
                  <div className={`gcal-column-header ${isToday ? 'today' : ''}`}>
                    <div className="gcal-column-header-day">{JOURS_COURTS[dateIdx]}</div>
                    <div className="gcal-column-header-date">{dayDate.getDate()}</div>
                    {absencesCount > 0 && (
                      <div className="text-xs text-red-400 mt-1">
                        {absencesCount} absent{absencesCount > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {/* Column Body */}
                  <div 
                    className="gcal-column-body" 
                    style={{ height: `${HOURS.length * 60}px`, position: 'relative', cursor: 'pointer' }}
                    onClick={() => { setCurrentDate(date); setPlanningView('jour'); }}
                  >
                    {/* Hour rows */}
                    {HOURS.map((hour) => (
                      <div 
                        key={hour}
                        className={`gcal-hour-row ${draggedSlot ? 'cursor-copy' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDropOnCell(e, draggedSlot?.technicien_id, date, hour)}
                      />
                    ))}

                    {/* Current time indicator */}
                    {isToday && (
                      <div 
                        className="gcal-current-time"
                        style={{ top: `${getCurrentTimePosition()}px` }}
                      />
                    )}

                    {/* Events - grouped by hour */}
                    {daySlots.map(slot => {
                      const tech = getTech(slot.technicien_id);
                      const techColor = getTechColor(slot.technicien_id);
                      const ot = getOT(slot.ordre_travail_id);
                      const client = ot ? getClient(ot.client_id) : null;
                      const eventStyle = getEventStyle(slot);

                      return (
                        <div
                          key={slot.id}
                          className="gcal-event"
                          draggable
                          onDragStart={(e) => handleDragStart(e, slot)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => { e.stopPropagation(); setSelectedSlot(slot); }}
                          style={{
                            ...eventStyle,
                            background: techColor.bg,
                            borderLeftColor: tech?.couleur_hex || techColor.border,
                            opacity: slot.statut === 'provisoire' ? 0.7 : 1
                          }}
                          data-testid={`event-${slot.id}`}
                        >
                          <div className="gcal-event-title" style={{ fontSize: '11px' }}>
                            {tech?.initiales} · {client?.nom?.substring(0, 10) || 'RDV'}
                          </div>
                          <div className="gcal-event-time">
                            {slot.duree_minutes / VT_TO_MIN} VT
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Slot Detail Panel */}
      {selectedSlot && (
        <div className="panel-overlay" onClick={() => setSelectedSlot(null)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Détail RDV</div>
              <button className="panel-close" onClick={() => setSelectedSlot(null)} data-testid="close-slot-detail">
                <X size={20} />
              </button>
            </div>

            {(() => {
              const tech = getTech(selectedSlot.technicien_id);
              const ot = getOT(selectedSlot.ordre_travail_id);
              const client = ot ? getClient(ot.client_id) : null;
              const moto = ot ? getMoto(ot.moto_id) : null;

              return (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded" style={{ background: 'var(--bg3)' }}>
                      <div className="tech-avatar" style={{ background: tech?.couleur_hex, width: 40, height: 40 }}>
                        {tech?.initiales}
                      </div>
                      <div>
                        <div className="font-bold">{tech?.prenom} {tech?.nom}</div>
                        <div className="text-sm text-[var(--text3)]">Technicien assigné</div>
                      </div>
                    </div>

                    <div className="grid-2 gap-4">
                      <div>
                        <div className="text-xs text-[var(--text3)]">Date</div>
                        <div className="font-medium">{fmtDate(selectedSlot.date)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[var(--text3)]">Heure</div>
                        <div className="font-medium">{selectedSlot.heure_debut}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[var(--text3)]">Durée</div>
                        <div className="font-medium">{selectedSlot.duree_minutes} min ({selectedSlot.duree_minutes / VT_TO_MIN} VT)</div>
                      </div>
                      <div>
                        <div className="text-xs text-[var(--text3)]">Statut</div>
                        <span className={`badge ${selectedSlot.statut === 'provisoire' ? 'badge-yellow' : selectedSlot.statut === 'confirme' ? 'badge-green' : 'badge-gray'}`}>
                          {selectedSlot.statut}
                        </span>
                      </div>
                    </div>

                    {ot && (
                      <div className="panel-section">
                        <div className="panel-section-title">Ordre de Travail</div>
                        <div className="p-3 rounded" style={{ background: 'var(--bg3)' }}>
                          <div className="font-mono font-bold">{ot.reference}</div>
                          <div className="text-sm">{client?.prenom} {client?.nom}</div>
                          <div className="text-sm text-[var(--text3)]">{moto?.marque} {moto?.modele}</div>
                          <div className="text-xs text-[var(--text3)] mt-2">{ot.description}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-6">
                    <button 
                      className="btn btn-ghost flex-1"
                      onClick={() => handleDeleteSlot(selectedSlot.id)}
                      data-testid="delete-slot-btn"
                    >
                      <Trash2 size={14} /> Supprimer
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* New Slot Modal */}
      {showNewSlotModal && (
        <div className="panel-overlay" onClick={() => setShowNewSlotModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Nouveau RDV</div>
              <button className="panel-close" onClick={() => setShowNewSlotModal(false)} data-testid="close-new-slot-modal">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Technicien</label>
                <select
                  className="input w-full"
                  value={newSlotForm.technicien_id}
                  onChange={(e) => setNewSlotForm({ ...newSlotForm, technicien_id: e.target.value })}
                  data-testid="new-slot-tech-select"
                >
                  <option value="">Sélectionner...</option>
                  {techsForPlanning.map(t => (
                    <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>
                  ))}
                </select>
              </div>

              <div className="grid-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Date</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={newSlotForm.date}
                    onChange={(e) => setNewSlotForm({ ...newSlotForm, date: e.target.value })}
                    data-testid="new-slot-date-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Heure</label>
                  <select
                    className="input w-full"
                    value={newSlotForm.heure_debut}
                    onChange={(e) => setNewSlotForm({ ...newSlotForm, heure_debut: e.target.value })}
                    data-testid="new-slot-time-select"
                  >
                    {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Durée (minutes)</label>
                <select
                  className="input w-full"
                  value={newSlotForm.duree_minutes}
                  onChange={(e) => setNewSlotForm({ ...newSlotForm, duree_minutes: parseInt(e.target.value) })}
                  data-testid="new-slot-duration-select"
                >
                  <option value={30}>30 min (6 VT)</option>
                  <option value={60}>1h (12 VT)</option>
                  <option value={90}>1h30 (18 VT)</option>
                  <option value={120}>2h (24 VT)</option>
                  <option value={180}>3h (36 VT)</option>
                  <option value={240}>4h (48 VT)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Ordre de Travail (optionnel)</label>
                <select
                  className="input w-full"
                  value={newSlotForm.ordre_travail_id}
                  onChange={(e) => setNewSlotForm({ ...newSlotForm, ordre_travail_id: e.target.value })}
                  data-testid="new-slot-ot-select"
                >
                  <option value="">Sans OT</option>
                  {ots.filter(o => o.statut !== 'termine' && o.statut !== 'annule').map(ot => {
                    const client = getClient(ot.client_id);
                    return (
                      <option key={ot.id} value={ot.id}>
                        {ot.reference} - {client?.nom}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <button 
              className="btn btn-primary w-full mt-6"
              onClick={handleCreateSlot}
              disabled={!newSlotForm.technicien_id || !newSlotForm.date || !newSlotForm.heure_debut}
              data-testid="create-slot-btn"
            >
              <Plus size={14} /> Créer le RDV
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Planning;
